import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../../../components/form/CustomInput";
import CustomSelect from "../../../components/form/CustomSelect";
import CustomButton from "../../../components/form/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { update_load } from "../../../store/loads/action";
import { clientsResource, getScheduls, getStatuses, trailerTrucksResource, usersResource } from "../../../helpers/configRoutes/backend_helper";
import { showFormErrors } from "../../../helpers/showFormErrors";
import LoadConceptPayments from "../payments/LoadConceptPayments";
import { currencyUSD } from "../../../helpers/forms/formater";

const StepOne = ({
    onNext,
    onCancel
}) => {
    const dispatch = useDispatch()
    const { load, validationStepOne } = useSelector((state) => state.Load)

    //Catalogos para select
    const [clients, setClients] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [loadSchedules, setLoadSchedules] = useState([]);
    const [operators, setOperators] = useState([]);
    const [trucks, setTrucks] = useState([]);
    const [trailers, setTrailers] = useState([]);

    useEffect(() => {
        const getCatalogs = async () => {
            try {
                const trucksResponse = await trailerTrucksResource("get", {
                    params: { select: true, truck: true },
                });
                if (trucksResponse?.items) setTrucks(trucksResponse.items);

                const trailerResponse = await trailerTrucksResource("get", {
                    params: { select: true },
                });
                if (trailerResponse?.items) setTrailers(trailerResponse.items);

                const operatorsResponse = await usersResource("get", {
                    params: { operator: true, select: true, load: true },
                });
                if (operatorsResponse?.users) setOperators(operatorsResponse.users);

                const statusesResponse = await getStatuses({
                    params: { model: "Load" },
                });
                if (statusesResponse?.statuses)
                    setStatuses(statusesResponse.statuses);

                const schedulsResponse = await getScheduls();
                if (schedulsResponse?.scheduls)
                    setLoadSchedules(schedulsResponse.scheduls);

                const clientsResponse = await clientsResource("get", {
                    params: { select: true },
                });
                if (clientsResponse?.clients) setClients(clientsResponse.clients);
            } catch (e) {
                console.error("Error loading catalogs", e);
            }
        };
        getCatalogs();
    }, []);

    const validation = useFormik({
        initialValues: load,
        enableReinitialize: false,
        validationSchema: Yup.object(validationStepOne),
        onSubmit: (values) => {
            dispatch(update_load(values))
            onNext()
        },
    });

    const { values, errors, handleChange } = validation;

    const getEquipmentMeta = async (equipmentId, type) => {
        if (!equipmentId) {
            validation.setFieldValue(
                type === "truck" ? "current_mileage_truck" : "current_mileage_trailer",
                "",
                false
            );
            validation.setFieldValue(
                type === "truck" ? "truck_last_load" : "trailer_last_load",
                null,
                false
            );
            return {
                currentMileage: "",
                lastLoadAssignment: null,
            };
        }

        try {
            const response = await trailerTrucksResource(
                "show",
                {
                    params: {
                        include_last_load: true,
                        exclude_load_id: values.id || "",
                    },
                },
                equipmentId
            );

            const mileageField = type === "truck" ? "current_mileage_truck" : "current_mileage_trailer";
            const metaField = type === "truck" ? "truck_last_load" : "trailer_last_load";

            validation.setFieldValue(
                mileageField,
                response?.items?.current_mileage ?? "",
                false
            );
            validation.setFieldValue(
                metaField,
                response?.items?.last_load_assignment || null,
                false
            );

            return {
                currentMileage: response?.items?.current_mileage ?? "",
                lastLoadAssignment: response?.items?.last_load_assignment || null,
            };
        } catch (error) {
            console.error(`Error loading ${type} metadata`, error);
            return {
                currentMileage: "",
                lastLoadAssignment: null,
            };
        }
    };

    const handleEquipmentChange = async (event, type) => {
        const previousTrailerId = values.trailer_id;
        handleChange(event);

        await getEquipmentMeta(event.target.value, type);

        let nextStops = values.stops || [];

        if (type === "trailer") {
            nextStops = (nextStops || []).map((stop) => ({
                ...stop,
                trailer_id:
                    !stop?.trailer_id || String(stop.trailer_id) === String(previousTrailerId)
                        ? event.target.value
                        : stop.trailer_id,
            }));
        }

        validation.setFieldValue("stops", nextStops, false);
    };

    const renderLastLoadHint = (assignment, typeLabel) => {
        if (!assignment?.last_stop?.name) return null;

        return (
            <p className="mt-1 text-[11px] text-cs-muted">
                Last {typeLabel.toLowerCase()} stop: {assignment.last_stop.name}
            </p>
        );
    };

    useEffect(() => {
        if (!values.truck_id) return;

        getEquipmentMeta(values.truck_id, "truck");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.truck_id, values.id]);

    useEffect(() => {
        if (!values.trailer_id) return;

        getEquipmentMeta(values.trailer_id, "trailer");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.trailer_id, values.id]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!values.payments || !values.payments.length) {
            validation.setFieldValue('charges', 0, false);
            return;
        }

        const total = values.payments.reduce((sum, p) => {
            const amount = parseFloat(p.amount) || 0;
            return sum + amount;
        }, 0);

        validation.setFieldValue('charges', total, false);
    }, [values.payments]); // eslint-disable-line react-hooks/exhaustive-deps


    return (
        <>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                    return false;
                }}
                className="space-y-4"
            >
                <div className="space-y-6">

                    {/* Título del paso */}
                    <div>
                        <h2 className="text-lg font-semibold text-cs-dark">
                            Load & equipment
                        </h2>
                        <p className="text-sm text-cs-muted mt-1">
                            Define client, charges and assign truck / trailer / operator.
                        </p>
                    </div>

                    {/* LOAD DETAILS */}
                    <div className="space-y-3">
                        <h3 className="text-xs font-semibold text-cs-muted uppercase tracking-wide">
                            Load details
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <CustomSelect
                                label="Client"
                                options={clients}
                                name="client_id"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={errors.client_id}
                                value={values.client_id}
                                handleChange={handleChange}
                            />

                            <CustomInput
                                label="Client Work Order"
                                type="text"
                                name="client_work_order"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={errors.client_work_order}
                                value={values.client_work_order || ""}
                                handleChange={handleChange}
                                placeholder="Optional"
                                maxLength={100}
                            />

                            <CustomSelect
                                label="Status"
                                options={statuses}
                                name="status_id"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={errors.status_id}
                                value={values.status_id}
                                handleChange={handleChange}
                            />

                            <CustomSelect
                                label="Load schedule"
                                options={loadSchedules}
                                name="schedule_id"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={errors.schedule_id}
                                value={values.schedule_id}
                                handleChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* EQUIPMENT */}
                    <div className="space-y-3">
                        <h3 className="text-xs font-semibold text-cs-muted uppercase tracking-wide">
                            Equipment
                        </h3>

                        {/* Fila 1: operador + truck + mileage truck */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                            <div className="md:col-span-1">
                                <CustomSelect
                                    label="Operator"
                                    options={operators}
                                    name="operator_id"
                                    labelClassName="text-xs font-medium text-cs-dark"
                                    error={errors.operator_id}
                                    value={values.operator_id}
                                    handleChange={handleChange}
                                />
                            </div>

                            <div>
                                <CustomSelect
                                    label="Truck"
                                    options={trucks}
                                    name="truck_id"
                                    labelClassName="text-xs font-medium text-cs-dark"
                                    error={errors.truck_id}
                                    value={values.truck_id}
                                    handleChange={(event) => handleEquipmentChange(event, "truck")}
                                />
                                {renderLastLoadHint(values.truck_last_load, "Truck")}
                            </div>

                            <div>
                                <CustomSelect
                                    label="Trailer"
                                    options={trailers}
                                    name="trailer_id"
                                    labelClassName="text-xs font-medium text-cs-dark"
                                    error={errors.trailer_id}
                                    value={values.trailer_id}
                                    handleChange={(event) => handleEquipmentChange(event, "trailer")}
                                />
                                {renderLastLoadHint(values.trailer_last_load, "Trailer")}
                            </div>

                            <CustomInput
                                label="Mileage (truck)"
                                type="text"
                                name="current_mileage_truck"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={errors.current_mileage_truck}
                                value={values.current_mileage_truck}
                                handleChange={handleChange}
                            />

                            <CustomInput
                                label="Mileage (trailer)"
                                type="text"
                                name="current_mileage_trailer"
                                labelClassName="text-xs font-medium text-cs-dark"
                                error={errors.current_mileage_trailer}
                                value={values.current_mileage_trailer}
                                handleChange={handleChange}
                            />
                        </div>
                        <LoadConceptPayments load={validation} />
                        <div className="flex justify-end mt-4">
                            <div className="bg-gray-50 border rounded-lg px-4 py-2 text-sm">
                                <span className="text-cs-muted">Total load:</span>
                                <span className="ml-2 font-semibold text-cs-dark">
                                    {currencyUSD(values.charges)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6 pt-4 border-t border-cs-border">
                    <div className="text-xs text-cs-muted">
                        Step 1 of 3 – Basic load info.
                    </div>
                    <div className="flex justify-end gap-3">
                        <CustomButton
                            type="button"
                            onClick={onCancel}
                            className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              bg-cs-danger hover:bg-cs-primary-hover
              text-white text-sm font-medium
              shadow-md hover:shadow-lg
              transition-all
            "
                        >
                            Cancel
                        </CustomButton>

                        <CustomButton
                            type={"submit"}
                            className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              bg-cs-primary hover:bg-cs-primary-hover
              text-white text-sm font-medium
              shadow-md hover:shadow-lg
              transition-all
            "
                        >
                            Next step
                            <span className="material-symbols-outlined text-sm">
                                arrow_forward
                            </span>
                        </CustomButton>
                    </div>

                </div>
            </form>
        </>
    );
};

export default StepOne;
